//#region Imports
import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
    PaymentsFeatureName,
    PaymentsFeatureAdapter,
} from './payments.reducer';
import { BankAccountPay, NextPayment, PaymentData, PaymentsEntityState } from './../../interfaces';
import { looksLikeBankName } from './../../utils';

//#region Entity selectors consts

const currentDate = new Date();

const EntitySelectors = PaymentsFeatureAdapter.getSelectors();

const getPaymentsLoadedState = ({ loaded }: PaymentsEntityState): boolean =>
    loaded;

const getNextPayment = ({ nextPayment }: PaymentsEntityState): NextPayment => nextPayment;

const getPaymentById =
    (id: string) =>
        ({ entities }: PaymentsEntityState): PaymentData | undefined =>
            entities[id];

const isNextPayment =
    (id: string) =>
        ({ nextPayment }: PaymentsEntityState): boolean => {
            return nextPayment?.id === id;
        };

//#region Selectors

export const PaymentsFeatureSelector =
    createFeatureSelector<PaymentsEntityState>(PaymentsFeatureName);

export const getPaymentsSelector = createSelector(
    PaymentsFeatureSelector,
    EntitySelectors.selectAll
);

export const getIsLoadedPaymentsSelector = createSelector(
    PaymentsFeatureSelector,
    getPaymentsLoadedState
);

export const getNextPaymentSelector = createSelector(
    PaymentsFeatureSelector,
    getNextPayment
);

/**
 * The bank account this lease's next payment will be drawn from (OPS-622).
 *
 * Both surfaces that name a bank for a lease — the lease card banner and the Payment methods
 * modal — read this, so they cannot show a different bank for the same lease. It is resolved
 * here rather than from the ROSE-wide bank list, which is not scoped to a lease, includes
 * closed and returned accounts, and carries Odessa's bank-branch identifier in place of a name.
 *
 * A lease with no payment history has nothing for rose-portal-service to resolve a name from
 * and the raw branch identifier arrives instead, hence the fallback to the newest payment that
 * names a real bank. The account is returned whole so that a name and a last-four can never be
 * paired from two different accounts.
 */
export const getNextPaymentBankSelector = createSelector(
    getNextPaymentSelector,
    getPaymentsSelector,
    (nextPayment, payments): Partial<BankAccountPay> => {
        const fromNextPayment = nextPayment?.bankAccount;
        if (looksLikeBankName(fromNextPayment?.bankName)) {
            return fromNextPayment;
        }
        const fromHistory = [...payments]
            .reverse()
            .find((payment) => looksLikeBankName(payment.bankAccount?.bankName));
        return fromHistory?.bankAccount ?? fromNextPayment ?? {};
    }
);

export const getIsNextPaymentSelector = (id: string) =>
    createSelector(PaymentsFeatureSelector, isNextPayment(id));

export const getPaymentByIdSelector = (id: string) =>
    createSelector(PaymentsFeatureSelector, getPaymentById(id));